<script lang="ts">
  import type { Component } from 'svelte';
  import MapPinIcon from 'phosphor-svelte/lib/MapPinIcon';
  import MapTrifoldIcon from 'phosphor-svelte/lib/MapTrifoldIcon';

  import { i18n } from '#lib/i18n.js';

  interface Props {
    body: string;
    latitude: number | null;
    longitude: number | null;
  }

  let { body, latitude, longitude }: Props = $props();
  let valid = $derived(
    latitude !== null &&
      longitude !== null &&
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      Math.abs(latitude) <= 90 &&
      Math.abs(longitude) <= 180
  );
  let href = $derived(valid ? `geo:${latitude},${longitude}` : undefined);
  let coordinates = $derived(
    !valid || latitude === null || longitude === null
      ? null
      : `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
  );
  let label = $derived(body.trim() || i18n.t('timeline.locationFallback'));

  interface MapProps {
    latitude: number;
    longitude: number;
    label: string;
  }

  let map = $state.raw<Component<MapProps> | null>(null);
  let loading = $state(false);
  let failed = $state(false);

  async function showMap(): Promise<void> {
    loading = true;
    failed = false;
    try {
      const module = await import('../media/LocationMap.svelte');
      map = module.default;
    } catch (error) {
      console.warn('[sable timeline] loading the map failed', error);
      failed = true;
    } finally {
      loading = false;
    }
  }
</script>

<div class="location">
  <a class="summary" {href} rel="external" aria-disabled={!valid || undefined}>
    <MapPinIcon class="pin" size={20} aria-hidden="true" />
    <span class="text">
      <span class="label">{label}</span>
      {#if coordinates}<span class="coordinates">{coordinates}</span>{/if}
    </span>
  </a>

  {#if valid && latitude !== null && longitude !== null}
    {#if map}
      {@const Map = map}
      <Map {latitude} {longitude} {label} />
    {:else}
      <button type="button" class="reveal" disabled={loading} onclick={showMap}>
        <MapTrifoldIcon size={16} aria-hidden="true" />
        {loading ? i18n.t('common.loadingMap') : i18n.t('timeline.locationShowMap')}
      </button>
      {#if failed}
        <p class="failed">{i18n.t('common.theMapCouldNotBeLoaded')}</p>
      {/if}
    {/if}
  {/if}
</div>

<style>
  .location {
    background: var(--surface-container);
    border: var(--border-width) solid var(--surface-container-line);
    border-radius: var(--radius);
    color: var(--surface-on-container);
    display: flex;
    flex-direction: column;
    gap: var(--space-200);
    max-width: var(--timeline-media-max);
    padding: var(--space-200) var(--space-300);
  }

  .summary {
    align-items: center;
    color: inherit;
    display: flex;
    gap: var(--space-200);
    text-decoration: none;
  }

  .summary:hover {
    text-decoration: underline;
  }

  .summary :global(.pin) {
    color: var(--primary-main);
    flex: none;
  }

  .text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .label {
    overflow-wrap: anywhere;
  }

  .coordinates {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    font-variant-numeric: tabular-nums;
  }

  .reveal {
    align-items: center;
    align-self: flex-start;
    background: none;
    border: none;
    border-radius: var(--radii-300);
    color: var(--primary-main);
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-size: var(--font-size-small);
    gap: var(--space-100);
    padding: var(--space-100) 0;
  }

  .reveal:disabled {
    color: var(--surface-var-on-container);
    cursor: progress;
  }

  .reveal:hover:not(:disabled) {
    text-decoration: underline;
  }

  .failed {
    color: var(--crit-main);
    font-size: var(--font-size-small);
    margin: 0;
  }
</style>
